/**
 * 반 점검표: 일괄 생성 결과에서 첫머리 겹침, 반 공유 표현, 상투어, 비슷한 학생 쌍을 찾는다.
 *
 * 기준은 운영 일괄 기록(8명 이상 반 2천 개)으로 맞췄다. 이 값이면 반 절반은 걸리는 학생이 0명이고,
 * 걸린 학생은 전체의 약 20%다. 백엔드 실험 스크립트(eval/exp/classcheck/class_check.py)와 같은 규칙이다.
 */

export const OPENING_CHARS = 12;
export const OPENING_MIN_STUDENTS = 3;
export const OPENING_MIN_SHARE = 0.15;
export const PHRASE_CHARS = 10;
export const PHRASE_MIN_SHARE = 0.5;
export const PHRASE_MIN_STUDENTS = 3;
export const PAIR_NGRAM = 4;
export const PAIR_MIN_JACCARD = 0.45;
export const SLOP_MIN_HITS = 6;
export const MAX_PHRASES = 5;

// backend/utils/saenggibu_slop.py SLOP_PHRASES 와 같은 목록 (긴 표현 먼저).
export const SLOP_PHRASES = [
  '견지함', '견지하며', '입증함', '증명함', '역설함', '규명함', '정립함', '도모함',
  '극대화함', '극대화하는', '조성함', '앞장섬', '두드러짐', '유감없이',
  '탁월함', '탁월하며', '탁월한', '매우 인상적임', '매우 뛰어남', '노력이 돋보임', '돋보임', '돋보이며',
  '역량을 입증함', '능력을 입증함', '역량을 발휘함', '역량을 드러냄', '역량을 갖춤', '능력을 갖춤',
  '태도를 견지함', '태도를 갖춤', '성장 가능성', '가능성을 보여줌', '잠재력을', '소양을', '자질을',
  '전문가로서의', '통찰력을', '통찰을', '깊이를 더함', '심도 있게', '심층적으로', '다각도로', '다각적인',
  '비판적 시각을', '분석적 태도를', '학문적', '학술적', '크게 기여함',
  '모습에서', '모습이 매우', '면모를', '중재자', '일원으로서', '구성원으로서', '매사', '매 순간',
  '수업 시간마다', '긍정적인 에너지를', '신뢰를 얻음', '화합을', '원만한', '분위기를 조성함',
  '것에 그치지 않고', '난제를', '난관을', '과업을', '공동의 목표를',
].sort((a, b) => b.length - a.length);

export const compactText = (text) => String(text || '').replace(/\s+/g, '');

const ngrams = (text, n) => {
  const body = compactText(text);
  const grams = new Set();
  for (let i = 0; i + n <= body.length; i += 1) grams.add(body.slice(i, i + n));
  return grams;
};

const jaccard = (a, b) => {
  if (a.size === 0 || b.size === 0) return 0;
  let common = 0;
  a.forEach((gram) => { if (b.has(gram)) common += 1; });
  return common / (a.size + b.size - common);
};

export const slopHits = (text) => {
  let body = String(text || '');
  const found = [];
  SLOP_PHRASES.forEach((phrase) => {
    const parts = body.split(phrase);
    if (parts.length > 1) {
      for (let i = 1; i < parts.length; i += 1) found.push(phrase);
      body = parts.join(' ');
    }
  });
  return found;
};

const overlaps = (a, b, minCommon = 5) => {
  for (let k = 0; k + minCommon <= a.length; k += 1) {
    if (b.includes(a.slice(k, k + minCommon))) return true;
  }
  return false;
};

const findOpeningGroups = (texts) => {
  const groups = new Map();
  texts.forEach((text, index) => {
    const key = compactText(text).slice(0, OPENING_CHARS);
    if (key.length < OPENING_CHARS) return;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(index);
  });
  const need = Math.max(OPENING_MIN_STUDENTS, OPENING_MIN_SHARE * texts.length);
  return [...groups.entries()]
    .filter(([, students]) => students.length >= need)
    .map(([opening, students]) => ({ opening, students }))
    .sort((a, b) => b.students.length - a.students.length);
};

const findSharedPhrases = (texts, contextText) => {
  const need = Math.max(PHRASE_MIN_STUDENTS, Math.ceil(texts.length * PHRASE_MIN_SHARE));
  const counts = new Map();
  texts.forEach((text, index) => {
    ngrams(text, PHRASE_CHARS).forEach((gram) => {
      if (!counts.has(gram)) counts.set(gram, new Set());
      counts.get(gram).add(index);
    });
  });
  const context = compactText(contextText);
  const picked = [];
  [...counts.entries()]
    .filter(([, students]) => students.size >= need)
    .sort((a, b) => (b[1].size - a[1].size) || (a[0] < b[0] ? -1 : 1))
    .some(([phrase, students]) => {
      if (picked.some(item => overlaps(phrase, item.phrase))) return false;
      picked.push({ phrase, students: [...students].sort((a, b) => a - b), isActivity: Boolean(context) && context.includes(phrase) });
      return picked.length >= MAX_PHRASES;
    });
  return picked;
};

const findSimilarPairs = (texts) => {
  const grams = texts.map(text => ngrams(text, PAIR_NGRAM));
  const pairs = [];
  for (let a = 0; a < texts.length; a += 1) {
    for (let b = a + 1; b < texts.length; b += 1) {
      const similarity = jaccard(grams[a], grams[b]);
      if (similarity >= PAIR_MIN_JACCARD) pairs.push({ a, b, similarity: Math.round(similarity * 1000) / 1000 });
    }
  }
  return pairs.sort((x, y) => y.similarity - x.similarity);
};

/**
 * texts: 학생별 결과 글. contextText: 평가계획서·요청사항·학생 원자료를 이어 붙인 글(활동명 표시용).
 * 반환 students 는 다르게 쓰면 좋은 학생만(각 묶음의 첫 학생은 그대로 둔다).
 */
export const analyzeClassOverlap = (texts = [], { contextText = '' } = {}) => {
  const list = (Array.isArray(texts) ? texts : []).map(text => String(text || ''));
  const usable = list.filter(text => compactText(text).length >= 20).length;
  if (usable < 3) return { openings: [], phrases: [], pairs: [], slop: [], students: [], studentCount: list.length };

  const openings = findOpeningGroups(list);
  const phrases = findSharedPhrases(list, contextText);
  const pairs = findSimilarPairs(list);
  const slop = list.map(text => slopHits(text));

  const byIndex = new Map();
  const flag = (index, reason, extra = {}) => {
    if (!byIndex.has(index)) byIndex.set(index, { index, reasons: [], opening: '', similarTo: null, slopWords: [] });
    const entry = byIndex.get(index);
    if (!entry.reasons.includes(reason)) entry.reasons.push(reason);
    Object.assign(entry, extra);
  };
  openings.forEach(group => group.students.slice(1).forEach(index => flag(index, 'opening', { opening: group.opening })));
  pairs.forEach(pair => flag(pair.b, 'similar', { similarTo: pair.a }));
  slop.forEach((hits, index) => {
    if (hits.length >= SLOP_MIN_HITS) flag(index, 'slop', { slopWords: [...new Set(hits)].slice(0, 4) });
  });

  const students = [...byIndex.values()].sort((x, y) => (y.reasons.length - x.reasons.length) || (x.index - y.index));
  return { openings, phrases, pairs, slop, students, studentCount: list.length };
};

/** '이 학생만 다르게'에 넘길 수정 요청. 다른 학생 글은 넣지 않고 피할 표현만 알려 준다. */
export const buildMakeDifferentInstruction = (entry, { text = '', phrases = [], category = '' } = {}) => {
  const lines = ['다른 학생 글과 겹치지 않게 이 학생 글만 다시 써 주세요. 사실과 활동은 그대로 두고 표현과 문장 구조만 바꿔 주세요.'];
  if (entry?.opening) lines.push(`첫 문장을 '${entry.opening}'처럼 시작하지 마세요.`);
  if (category === '진로') lines.push('첫 문장에서 희망 진로를 되풀이하지 말고, 학생이 실제로 한 활동으로 시작해 주세요.');
  const body = compactText(text);
  const avoid = phrases
    .filter(item => !item.isActivity && body.includes(item.phrase))
    .map(item => item.phrase)
    .slice(0, 3);
  const counts = new Map();
  slopHits(text).forEach(word => counts.set(word, (counts.get(word) || 0) + 1));
  const slopWords = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([word]) => word).slice(0, 4);
  const words = [...avoid, ...slopWords];
  if (words.length > 0) lines.push(`이 표현은 쓰지 마세요: ${words.join(', ')}.`);
  return lines.join(' ');
};
